/**
 * The plan's Resourcing status — the RM/ADMIN staffing status, distinct from
 * `PlanStatusControl`'s sales status. Sits beside the sales status badge at the top of
 * the plan, in the title row.
 *
 * Not set / In Review / Staffing save immediately on change, and roll back via the
 * mutation's own error toast on failure, same as every other inline edit in this app.
 *
 * **Staffed** is different (product decision 2026-10-06): it writes the plan's confirmed
 * Resources onto the Solution, so it is unavailable until every Plan Line is Confirmed and
 * picking it opens `PlanStaffingConfirmDialog` rather than saving. The server enforces
 * the same gate; the disabled item is only the early warning.
 */

import { useState } from "react";
import { PlanStaffingConfirmDialog } from "@/components/plans/PlanStaffingConfirmDialog";
import { STATUS_TRIGGER_CLASS, StatusDot } from "@/components/plans/PlanStatusControl";
import { resourcingStatusDotClass } from "@/components/plans/PlansTable";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useSetPlanResourcingStatus } from "@/hooks/use-resource-plans";
import { planLineStatus } from "@/lib/plan-line-status";
import { resourcingStatusLabel } from "@/lib/plan-status-labels";
import { cn } from "@/lib/utils";
import {
  type ResourcePlanDetailView,
  SETTABLE_PLAN_RESOURCING_STATUSES,
} from "@/schemas/resource-plans";

/** Radix `Select` cannot carry a `null` item value, so "Not set" needs a sentinel that
 * never collides with a real `PlanResourcingStatus`. */
const NOT_SET = "__not_set__";

type SettableStatus = (typeof SETTABLE_PLAN_RESOURCING_STATUSES)[number];

function isSettableStatus(value: string): value is SettableStatus {
  return SETTABLE_PLAN_RESOURCING_STATUSES.some((s) => s === value);
}

export function PlanResourcingControl({ plan }: { plan: ResourcePlanDetailView }) {
  const setStatus = useSetPlanResourcingStatus(plan.id);
  const [confirmStaffedOpen, setConfirmStaffedOpen] = useState(false);

  if (plan.canSetResourcingStatus !== true) {
    if (!plan.resourcingStatus) return null;
    return (
      <span className={cn(STATUS_TRIGGER_CLASS, "cursor-default hover:bg-transparent")}>
        <span className="text-muted-foreground">Resourcing:</span>
        <StatusDot className={resourcingStatusDotClass(plan.resourcingStatus)} />
        <span className="font-medium">{resourcingStatusLabel(plan.resourcingStatus)}</span>
      </span>
    );
  }

  // A split line (RP-24) is confirmed by its Shares covering every hour — the same reading
  // the server's Staffed check makes — so status, not the Suggested Resource flag, decides.
  const unconfirmed = plan.lines.filter((l) => planLineStatus(l) !== "Confirmed").length;
  const staffedBlocked = plan.resourcingStatus !== "Staffed" && unconfirmed > 0;

  return (
    <>
      <Select
        value={plan.resourcingStatus ?? NOT_SET}
        onValueChange={(value) => {
          if (value === NOT_SET) setStatus.mutate(null);
          else if (value === "Staffed") setConfirmStaffedOpen(true);
          else if (isSettableStatus(value)) setStatus.mutate(value);
        }}
        disabled={setStatus.isPending}
      >
        <SelectTrigger
          size="sm"
          // It sits in the page title's row, so it opts out of the title's size and weight,
          // and names itself — a bare "Not set" beside the plan name says nothing.
          className="w-auto gap-1.5 text-sm font-normal"
          aria-label="Set the Resourcing status"
          title="The Resource Manager's staffing status — separate from the plan's sales status."
        >
          <span className="text-muted-foreground">Resourcing:</span>
          <StatusDot className={resourcingStatusDotClass(plan.resourcingStatus)} />
          <SelectValue placeholder="Not set" className="font-medium" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NOT_SET}>Not set</SelectItem>
          {/* Reached only through Reject, which carries the reason. Shown so the current
              value reads correctly; picking another status moves the plan off it. */}
          {plan.resourcingStatus === "Returned" && (
            <SelectItem value="Returned" disabled>
              {resourcingStatusLabel("Returned")}
            </SelectItem>
          )}
          {SETTABLE_PLAN_RESOURCING_STATUSES.map((s) =>
            s === "Staffed" && staffedBlocked ? (
              <SelectItem
                key={s}
                value={s}
                disabled
                title="Confirm every Plan Line before setting the plan to Staffed."
              >
                {resourcingStatusLabel(s)}
                <span className="text-xs text-muted-foreground">
                  — {unconfirmed} line{unconfirmed === 1 ? "" : "s"} not confirmed
                </span>
              </SelectItem>
            ) : (
              <SelectItem key={s} value={s}>
                {resourcingStatusLabel(s)}
              </SelectItem>
            ),
          )}
        </SelectContent>
      </Select>
      <PlanStaffingConfirmDialog
        plan={plan}
        open={confirmStaffedOpen}
        onOpenChange={setConfirmStaffedOpen}
      />
    </>
  );
}
